import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import postal from 'postal';
import { useSpring, animated } from 'react-spring';
import useContributorStateStore from '../common/contributor-state-hook';
import AnimatedProfileFollowButton from '../../shared/animated-profile-follow-button';
import IconedButton from '../../shared/iconed-button';
import EnquiryContext from '../../shared/enquiry/context';
import useNavigationStateStore from '../../shared/navigation/navigation-state-hook';
import { recordStatisticsEvent } from '../../../services/statistics-service';
import NavMenu from '../../shared/navigation/nav-menu';

const height = 52;
const Container = styled(animated.div)`
  z-index: 20;
  width: 100vw;
  height: ${height}px;
  background: white;
  position: fixed;
  top: 66px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.1);

  &.new-nav {
    top: 104px;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  padding: 0;
  margin: 0 auto;

  ${props => props.theme.isLarge && `
    width: 85%;
  `};
  ${props => props.theme.isXLarge && `
    width: 953px;
  `};
  ${props => props.theme.isXXLarge && `
    width: 1200px;
  `};
  display: flex;
  align-items: center;
`;

const Buttons = styled.div`
  max-width: 186px;
  align-self: flex-end;
  display: flex;
  justify-content: space-between;

  & > button {
    margin: 0;
    margin-left: 8px;
    height: 36px;
    outline: none;
  }

  &:not(.discreet) > button:not([name="profile-follow-button"]) {
    width: 36px;
    padding: 6px;
    background-color: #273147;

    > span:not(.icon-lw) {
      display: none;
    }
  }

  & > button[name="profile-follow-button"] {
    width: 134px;

    .text-box {
      font-size: 14px;
    }
  }

  &&.discreet {
    > button[name="profile-follow-button"] {
      display: none;
    }

    > button:not([name="profile-follow-button"]) {
      background-color: #3c4966;
      width: 134px;
      > span.icon-lw-contact {
        padding: 3px;
      }
      & > span:not(.icon-lw) {
        font-size: 14px;
      }
    }
  }

  &&&& > button:not([name="profile-follow-button"]) {
    color: white;

    > span.icon-lw-contact {
      background-color: ${props => props.theme.bodyColor};
    }
  }
`;

const followTopic = {
  channel: 'micro-profile.commands',
  topic: 'follow-status',
};

// eslint-disable-next-line complexity
export default function StickyHeader({
  profile,
  followed,
  yOffset,
  isMyself,
  newNavEnabled,
}) {
  const { dispatch } = React.useContext(EnquiryContext);
  const [discreet, setDiscreet] = React.useState(followed);
  const showFeaturedWires = useNavigationStateStore(state => state.hasFeaturedWires);
  const showLatestWires = useContributorStateStore(state => state.hasLatestWires);
  const showMentions = useContributorStateStore(state => state.hasMentions);
  const showContact = !isMyself
    && _.includes(profile.activeFeatures, 'premium_enquiry_form')
    && _.get(profile, 'activePartnership');
  const navItems = [];

  if (showFeaturedWires) {
    navItems.push({ title: 'Featured Wires', sectionName: 'featured-wires' });
  }
  if (showLatestWires) {
    navItems.push({ title: 'Latest Wires', sectionName: 'latest-wires' });
  }
  if (showMentions) {
    navItems.push({ title: 'Mentions', sectionName: 'mentions' });
  }
  navItems.push({ title: 'About', sectionName: 'about' });
  if (showContact) {
    navItems.push({ title: 'Enquiries', sectionName: 'enquiry' });
  }

  const navMenuProps = {
    navItems,
    id: profile.id,
    statisticable: 'profile',
    url: `/contributors/${profile.slug}`,
  };

  const isVisible = profile.headlineGrab ? yOffset > 300 : yOffset > 180;

  const [styles, setStyles] = useSpring(() => ({ transform: `translateY(${-height}px)` }));
  setStyles({
    transform: `translateY(${isVisible ? 0 : -height}px)`,
    config: { tension: isVisible ? 170 : 350, clamp: true },
  });

  const followProps = {
    isProfileFollowedByCurrentUser: followed,
    profileId: profile.id,
    followEventName: 'sticky_header_follow_profile',
  };

  const contact = e => {
    e && e.preventDefault();

    recordStatisticsEvent({
      event: 'sticky_header_contact_profile',
      statisticable: 'profile',
      id: profile.id,
      page: window.location.pathname,
    });

    if (_.get(window, 'Livewire.currentUser.id')) {
      dispatch({ type: 'showEnquiryForm', payload: { scroll: true, offset: -200 } });
    } else {
      window.location = '/sign_up?return_to_section=enquiry';
    }
  };

  React.useEffect(() => () => postal.unsubscribe(followTopic), []);

  React.useEffect(() => {
    postal.subscribe({
      ...followTopic,
      callback: d => d.id === profile.id && setDiscreet(followed && d.following),
    });
  }, [profile.id, followed]);

  return (
    <Container style={styles} className={newNavEnabled ? 'new-nav' : ''}>
      <Content>
        <NavMenu {...navMenuProps} />
        {!isMyself && (
          <Buttons className={classNames({ discreet })}>
            <AnimatedProfileFollowButton {...followProps} />
            {!!showContact && (
              <IconedButton type="button" className="btn" onClick={contact}>
                <span className="icon-lw icon-lw-contact" />
                <span>contact</span>
              </IconedButton>
            )}
          </Buttons>
        )}
      </Content>
    </Container>
  );
}

StickyHeader.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
    activeFeatures: PropTypes.arrayOf(PropTypes.string),
    headlineGrab: PropTypes.string,
  }).isRequired,
  followed: PropTypes.bool.isRequired,
  yOffset: PropTypes.number.isRequired,
  isMyself: PropTypes.bool.isRequired,
  newNavEnabled: PropTypes.bool.isRequired,
};
